<template>
  <div class="gs-player-section">
    <video-format-picker v-if="selectedFormat" :value="selectedFormat" @input="changeFormat" />
    <gs-field
      :label="$t('frontend_groovevideo_videos_step1_video_size')"
      hint="Responsive fills whatever space the page gives the player."
    >
      <select
        data-field="dimension-type"
        class="gs-player-section__input"
        v-model="model.video_dimensions.type"
        @change="changeDimension()"
      >
        <option value="1">{{ $t('frontend_groovevideo_videos_step1_responsive') }}</option>
        <option value="2">{{ $t('frontend_groovevideo_videos_step1_fixed') }}</option>
      </select>
    </gs-field>

    <!--
      Width and height are required by the same rule step2 carried, whatever the
      size type is, so the pair is also revealed when that rule is what blocks
      a save — otherwise the highlighted field would be invisible.
    -->
    <div v-if="isFixed || dimensionsBlocked" class="gs-player-section__pair">
      <gs-field :label="$t('frontend_groovevideo_videos_step1_width')" hint="In pixels." :error="widthError">
        <input
          data-field="width"
          type="text"
          inputmode="numeric"
          class="gs-player-section__input"
          :class="{ 'gs-player-section__input--error': widthError }"
          :value="model.video_dimensions.width"
          @input="setDimension('width', $event.target.value)"
        />
      </gs-field>

      <gs-field :label="$t('frontend_groovevideo_videos_step1_height')" hint="In pixels." :error="heightError">
        <input
          data-field="height"
          type="text"
          inputmode="numeric"
          class="gs-player-section__input"
          :class="{ 'gs-player-section__input--error': heightError }"
          :value="model.video_dimensions.height"
          @input="setDimension('height', $event.target.value)"
        />
      </gs-field>
    </div>
  </div>
</template>

<script>
import VideoFormatPicker from '@/components/library/VideoFormatPicker.vue';
import { videoFormat, setVideoFormat } from '@/player/videoFormat';
import { required } from 'vuelidate/lib/validators';

export default {
  name: 'SizeSection',
  components: { VideoFormatPicker },

  props: {
    model: {
      type: Object,
      required: true,
    },
  },

  data() {
    return {
      debounceTimer: null,
    };
  },

  // Ported from step2, unchanged.
  validations: {
    model: {
      video_dimensions: {
        width: {
          required,
        },
        height: {
          required,
        },
      },
    },
  },

  computed: {
    selectedFormat() { return videoFormat(this.model); },
    isFixed() {
      // eslint-disable-next-line eqeqeq
      return this.model.video_dimensions.type == 2;
    },

    dimensionsBlocked() {
      return this.$v.model.video_dimensions.$invalid && this.$v.$dirty;
    },

    widthError() {
      return this.$v.model.video_dimensions.width.$error ? 'Enter a width in pixels.' : '';
    },

    heightError() {
      return this.$v.model.video_dimensions.height.$error ? 'Enter a height in pixels.' : '';
    },
  },

  beforeDestroy() {
    if (this.debounceTimer) clearTimeout(this.debounceTimer);
  },

  methods: {
    changeFormat(ratio) { setVideoFormat(this.model, ratio); this.changeDimension(); },
    setDimension(key, value) {
      this.model.video_dimensions[key] = value;
      if (this.selectedFormat && Number(value) > 0) {
        const [w, h] = this.selectedFormat.split(':').map(Number);
        this.model.video_dimensions[key === 'width' ? 'height' : 'width'] = Math.round(Number(value) * (key === 'width' ? h / w : w / h));
      }
      this.$emit('updateModel', this.model);
      // step2 debounced these two fields by a second: every change remounts the
      // real player, which is far too much work for one keystroke.
      if (this.debounceTimer) clearTimeout(this.debounceTimer);
      this.debounceTimer = setTimeout(() => {
        this.debounceTimer = null;
        this.changeDimension();
      }, 1000);
    },

    // Ported from step2.
    changeDimension() {
      const dimensionObject = {
        type: this.model.video_dimensions.type,
        height: this.model.video_dimensions.height,
        width: this.model.video_dimensions.width,
      };
      this.$store.dispatch('videoDimension', dimensionObject);
      this.$store.dispatch('videoComponentKey');
      this.$emit('updateModel', this.model);
      this.$emit('forceRerender');
    },

    validate() {
      this.$v.$touch();
      const isValid = !this.$v.$invalid;
      this.$emit('on-validate', isValid);
      return isValid;
    },
  },
};
</script>

<style scoped>
.gs-player-section {
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding-top: 14px;
}

.gs-player-section__pair {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 16px;
}

.gs-player-section__input {
  display: block;
  width: 100%;
  min-height: 40px;
  padding: 10px 12px;
  color: var(--gs-foreground);
  background: var(--gs-input);
  border: 1px solid var(--gs-border);
  border-radius: var(--gs-radius-md);
  font-size: 14px;
  line-height: 1.4;
}

.gs-player-section__input:focus {
  border-color: var(--gs-primary);
  outline: none;
  box-shadow: var(--gs-primary-shadow);
}

.gs-player-section__input--error {
  border-color: var(--gs-danger);
}

@media (max-width: 640px) {
  .gs-player-section__pair {
    grid-template-columns: minmax(0, 1fr);
  }
}
</style>
